import React, { useEffect, useState, useRef } from 'react';
import { Modal, Box, Typography, TextField, Button } from '@mui/material';

const PricingPage = () => {
    const [takeoffs, setTakeoffs] = useState([]);
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortAsc, setSortAsc] = useState(true);
    const [showArchived, setShowArchived] = useState(false);
    const [expandedRow, setExpandedRow] = useState(null);
    const [materials, setMaterials] = useState({});
    const [groupFilter, setGroupFilter] = useState('');
    const [subgroupFilter, setSubgroupFilter] = useState('');
    const [multiplier, setMultiplier] = useState(() => {
        return localStorage.getItem('rebateMultiplier') || 33;
    });
    const containerRef = useRef(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // Password state
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const fetchData = async (reset = false) => {
        const query = `?archived=${showArchived}`;
        const res = await fetch(`https://adam.app.autofront.com.au/api/takeoffs${query}`);
        const data = await res.json();
        if (reset) {
            setTakeoffs(data);
        } else {
            setTakeoffs((prev) => [...prev, ...data]);
        }
        setHasMore(data.length > 0);
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchData(true);
        }
    }, [isAuthenticated, showArchived]); // Ensure dependencies are correct

    useEffect(() => {
        const handleScroll = () => {
            if (!hasMore || !containerRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 10) {
                setPage((prev) => prev + 1);
            }
        };
        containerRef.current?.addEventListener('scroll', handleScroll);
        return () => containerRef.current?.removeEventListener('scroll', handleScroll);
    }, [hasMore]);

    useEffect(() => {
        if (page > 1 && isAuthenticated) fetchData();
    }, [page, isAuthenticated]);

    const handlePasswordSubmit = () => {
        if (password === 'adamprice!') {
            setIsAuthenticated(true);
            setError(''); // Clear any previous error
        } else {
            setError('Incorrect password. Please try again.');
        }
    };

    if (!isAuthenticated) {
        return (
            <Modal open={!isAuthenticated}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 300,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Enter Password
                    </Typography>
                    <TextField
                        type="password"
                        label="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    {error && (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            {error}
                        </Typography>
                    )}
                    <Button variant="contained" onClick={handlePasswordSubmit}>
                        Submit
                    </Button>
                </Box>
            </Modal>
        );
    }

    const formatCurrency = (value) =>
        `$ ${Number(value).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;

    const dedupedTakeoffs = Object.values(
        takeoffs.reduce((acc, t) => {
            if (!acc[t.id]) acc[t.id] = t;
            return acc;
        }, {})
    );

    const filtered = dedupedTakeoffs
        .filter(
            (t) =>
                (!groupFilter || t.group_name === groupFilter) &&
                (!subgroupFilter || t.subgroup_name === subgroupFilter) &&
                [t.name, t.group_name, t.subgroup_name].some((field) =>
                    field?.toLowerCase().includes(search.toLowerCase())
                )
        )
        .sort((a, b) => {
            const valA = a[sortField]?.toString().toLowerCase() || '';
            const valB = b[sortField]?.toString().toLowerCase() || '';
            return sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
        });

    const toggleRow = async (id) => {
        if (expandedRow === id) {
            setExpandedRow(null);
        } else {
            setExpandedRow(id);
            if (!materials[id]) {
                const res = await fetch(
                    `https://adam.app.autofront.com.au/api/takeoff-materials/${id}`
                );
                const data = await res.json();
                setMaterials((prev) => ({ ...prev, [id]: data }));
            }
        }
    };

    const handleSort = (field) => {
        if (sortField === field) setSortAsc(!sortAsc);
        else {
            setSortField(field);
            setSortAsc(true);
        }
    };

    const exportCSV = () => {
        const query = `?archived=${showArchived}`;
        window.open(
            `https://adam.app.autofront.com.au/api/takeoffs/export${query}`,
            '_blank'
        );
    };

    const uniqueGroups = [
        ...new Set(takeoffs.map((t) => t.group_name).filter(Boolean)),
    ];
    const uniqueSubgroups = [
        ...new Set(
            takeoffs
                .filter((t) => t.group_name === groupFilter)
                .map((t) => t.subgroup_name)
                .filter(Boolean)
        ),
    ];

    useEffect(() => {
        localStorage.setItem('rebateMultiplier', multiplier);
    }, [multiplier]);

    return (
        <div style={{ padding: '20px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold' }}>
                📦 Takeoff Pricing
            </h2>
            {/* Rest of the page content remains unchanged */}
        </div>
    );
};

export default PricingPage;